import { Injectable } from '@angular/core';
import flatten from 'lodash-es/flatten';
import groupBy from 'lodash-es/groupBy';
import sortBy from 'lodash-es/sortBy';
import union from 'lodash-es/union';
import { combineLatest, defer, EMPTY, from, merge, Observable, of, ReplaySubject, Subject } from 'rxjs';
import { defaultIfEmpty, first, map, mergeMap, reduce, shareReplay, switchMap, toArray } from 'rxjs/operators';

import { CachingService } from '../caching.service';
import { ApiParams, CoreApiService, queryAll } from '../core-api.service';
import { LoggingService } from '../logging.service';
import { NotificationService } from '../notification.service';
import { ProfileService } from '../profile/profile.service';
import { getContainerProjectId, getRoleMatrix } from '../shared/container.utils';
import { ContainerAccess } from '../shared/enums/container-access.enum';
import { lockedProjectProhibitedActions, PermissionAction } from '../shared/enums/permission-action.enum';
import { RequestState } from '../shared/enums/request-state.enum';
import { SiteRole } from '../shared/enums/site-role.enum';
import { Container } from '../shared/models/container.model';
import { Device } from '../shared/models/device.model';
import { Group, GroupOption } from '../shared/models/group.model';
import { Option } from '../shared/models/option.model';
import { Project, ProjectSettings, ProjectWithStats } from '../shared/models/project.model';
import { Role } from '../shared/models/role.model';
import { SearchResult } from '../shared/models/search/search-results.model';
import { User } from '../shared/models/user.model';
import { firstValueFrom } from '../shared/observable.utils';
import { CvatExportFormats } from '../shared/models/cvat.model';

export interface ProjectRoles {
  [projectId: string]: string[]
}

export interface ProjectPermissions {
  [projectId: string]: PermissionAction[]
}

export type UserOption = User & Option

@Injectable()
export class ProjectsService extends CachingService {
  private projects = {
    state: RequestState.Empty,
    subject: new ReplaySubject<ProjectWithStats[]>(1),
    request: this.profileService.exhaustiveMode$.pipe(
      switchMap(exhaustive => {
        const params = { join_avatars: true, stats: true };
        return queryAll(this.coreApi.projects.query, exhaustive ? { ...params, exhaustive: true } : params) as Observable<ProjectWithStats[]>;
      }),
    ),
  }
  readonly projects$ = this.projects.subject.asObservable()
  readonly projectRoles$: Observable<ProjectRoles> = defer(() => combineLatest([
    this.getProjects(),
    this.profileService.getProfile(),
  ])).pipe(
    map(([projects, profile]) => {
      if (!profile?._id) {
        return {};
      }
      return Object.assign({}, ...projects.map(project => {
        const permission = project.permissions.find(permission => permission._id === profile._id);
        const roleIds = permission ? permission.role_ids : [];
        return { [project._id]: roleIds };
      }));
    }),
    shareReplay(1),
  )
  readonly projectPermissions$: Observable<ProjectPermissions> = defer(() => combineLatest([
    this.getRoles(),
    this.getProjects(),
    this.projectRoles$,
    this.profileService.getProfile(),
  ])).pipe(
    map(([roles, projects, projectRoles, profile]) => {
      if (!profile?._id) {
        return {};
      }
      const lockedProjectIds = new Set(
        projects.filter(project => project.locked).map(project => project._id)
      );
      const allActions = Object.values(PermissionAction);
      // reduce maximum permissions if a project is locked, even for site admins
      const lockedActions = allActions.filter(action => !lockedProjectProhibitedActions.has(action));
      const isSiteAdmin = profile ? SiteRole.isSiteAdmin(profile) : false;
      const result = Object.assign({}, ...Object.entries(projectRoles).map(([projectId, projectRoles]) => {
        const maxActions = lockedProjectIds.has(projectId) ? lockedActions : allActions;
        return {
          [projectId]: isSiteAdmin ? maxActions : maxActions.filter(action => projectRoles.some(roleId => {
            const role = roles.find(r => r._id === roleId);
            return role ? role.actions.includes(action) : false;
          })),
        };
      }));
      return result;
    }),
    shareReplay(1),
  )

  private groups = {
    state: RequestState.Empty,
    subject: new ReplaySubject<Group[]>(1),
    request: this.profileService.exhaustiveMode$.pipe(
      switchMap(exhaustive => queryAll(this.coreApi.groups.query, exhaustive ? { exhaustive: true } : undefined))
    ),
  }
  readonly groups$ = this.groups.subject.asObservable()

  private roles = {
    state: RequestState.Empty,
    subject: new ReplaySubject<Role[]>(1),
    request: queryAll(this.coreApi.roles.query),
  }
  readonly roles$ = this.roles.subject.asObservable()

  /**
   * Emits when getProjects is called with refresh: true
   * Useful for cases where a component updates a project, and we need another to catch that signal (without a subscription to projects$)
   */
  readonly refreshProjects$ = new Subject<void>()

  private users = {
    state: RequestState.Empty,
    subject: new ReplaySubject<User[]>(1),
    request: queryAll(this.coreApi.users.query),
  }
  readonly users$ = this.users.subject.asObservable()

  private devices = {
    state: RequestState.Empty,
    subject: new ReplaySubject<Device[]>(1),
    request: queryAll(this.coreApi.devices.query),
  }
  readonly devices$ = this.devices.subject.asObservable()

  readonly userOptions$: Observable<UserOption[]> = defer(() => this.getUsers()).pipe(
    map(users => sortBy(users.map(user => {
      return {
        ...user,
        value: user._id,
        label: `${user.firstname ?? ''} ${user.lastname ?? ''}`.trim() || user.email || user._id,
      };
    }), user => user.label.toLowerCase())),
    shareReplay(1),
  )

  reloadProject$ = new Subject<void>()
  openAddDataModal$ = new Subject<void>()

  private _visitedProjects$ = new Subject<string[]>() // update projects without reloading the profile
  readonly visitedProjects$: Observable<string[]> = merge(
    defer(() => this.profileService.getProfile().pipe(map(profile => profile?.visited_projects ?? []))),
    this._visitedProjects$,
  ).pipe(shareReplay(1))

  constructor(
    private coreApi: CoreApiService,
    private logger: LoggingService,
    private profileService: ProfileService,
    private notifications: NotificationService,
  ) {
    super();
  }

  getProjects({ refresh = false } = {}): Observable<ProjectWithStats[]> {
    if (refresh) {
      this.refreshProjects$.next();
    }
    return this.getCacheableResource(this.projects, this.projects$, { refresh });
  }

  getGroups({ refresh = false } = {}): Observable<Group[]> {
    return this.getCacheableResource(this.groups, this.groups$, { refresh });
  }

  getRoles({ refresh = false } = {}): Observable<Role[]> {
    return this.getCacheableResource(this.roles, this.roles$, { refresh });
  }

  getUsers({ refresh = false } = {}): Observable<User[]> {
    return this.getCacheableResource(this.users, this.users$, { refresh });
  }

  getProject(projectId: string, params?: ApiParams): Observable<Project> {
    return this.coreApi.projects.get(projectId, params);
  }

  getDevices({ refresh = false } = {}): Observable<Device[]> {
    return this.getCacheableResource(this.devices, this.devices$, { refresh });
  }

  getProjectSettings(projectId: string, params?: ApiParams): Observable<ProjectSettings> {
    return this.coreApi.projects.settings.get(projectId, params);
  }

  getCvatExportFormats(): Observable<CvatExportFormats[]> {
    return this.coreApi.projects.cvatExportFormats.get()
  }

  getProjectGroup(projectId: string, params?: ApiParams): Observable<Group> {
    return combineLatest([
      this.getProject(projectId, params),
      this.getGroups(),
    ]).pipe(
      map(([project, groups]) => groups.find((group) => group._id === project.group))
    );
  }

  // DEPRECATED: use projects-query-service instead
  private getCachedProject(projectId: string, options = { refresh:  false }): Observable<Project> {
    return this.getProjects(options).pipe(
      first(),
      switchMap(projects => {
        const project = projects.find(({ _id }) => _id === projectId);
        return project ? of(project) : this.coreApi.projects.get(projectId);
      }),
    );
  }

  /**
   * Returns a list of users with ALL the requested permissions for a given project
   */
  getProjectUsersWithPermission(projectId: string, permission: PermissionAction | PermissionAction[]): Observable<User[]> {
    return combineLatest([
      this.getUsers(),
      this.getRoles(),
      this.getCachedProject(projectId),
    ]).pipe(map(([users, roles, project]) => {
      if (!project) {
        return [];
      }
      const usersWithRoles = project.permissions.map(permission => ({
        user: users.find(user => user._id === permission._id),
        roles: permission.role_ids.map(role => roles.find(r => r._id === role)),
      }));
      return usersWithRoles.filter(({ roles }) => {
        const neededPermissions = Array.isArray(permission) ? permission : [permission];
        return neededPermissions.every(perm => flatten(roles.map(role => role.actions)).includes(perm));
      }).map(({ user }) => user);
    }));
  }

  /**
   * Checks if the currently logged-in user has ALL specified permission(s) for a given project.
   */
  hasProjectUserPermission(projectId: string, permission: PermissionAction | PermissionAction[]): Observable<boolean>{
    return combineLatest([
      this.getProjectUsersWithPermission(projectId, permission),
      this.profileService.getProfile(),
    ]).pipe(
      map(([users, currentUser]) => users.some(user => user._id === currentUser._id)),
    )
  }

  getCachedGroup(groupId: string): Observable<Group> {
    return this.getGroups().pipe(
      first(),
      switchMap((groups) => {
        const group = groups.find(({ _id }) => _id === groupId);
        return group ? of(group) : this.coreApi.groups.get(groupId);
      }),
    );
  }

  getCachedUser(userId: string): Observable<User> {
    return this.getUsers().pipe(
      first(),
      switchMap((users) => {
        const user = users.find(({ _id }) => _id === userId);
        return user ? of(user) : this.coreApi.users.get(userId);
      }),
    );
  }

  getGroupsWithAccess(access: ContainerAccess, allGroups = false): Observable<Group[]> {
    const profile$ = this.profileService.getProfile();
    const groups$ = combineLatest([
      profile$,
      this.profileService.exhaustiveMode$,
    ]).pipe(
      switchMap(([user, exhaustive]) => {
        // ensure we're getting all groups if we're a site admin
        if (allGroups && SiteRole.isSiteAdmin(user)) {
          return exhaustive ? this.getGroups() : queryAll(this.coreApi.groups.query, { exhaustive: true });
        }
        return this.getGroups();
      })
    );
    return combineLatest([groups$, profile$]).pipe(
      map(([groups, user]) => {
        return groups.filter(group => {
          const roleMatrix = getRoleMatrix(group, user);
          return roleMatrix[access];
        });
      }),
    );
  }

  getLockedProjectIds(): Observable<string[]> {
    return this.getProjects().pipe(
      map(projects => projects.filter(project => project.locked).map(project => project._id))
    );
  }

  getProjectsWithPermissions(actions: PermissionAction[]): Observable<Project[]> {
    return this.getProjects().pipe(
      // break array into an emission for each
      switchMap(projects => from(projects).pipe(
        mergeMap(project => this.getProjectPermissions(project._id).pipe(
          // filter out projects we don't have required permission for
          switchMap(permissions => actions.every(action => permissions.includes(action)) ? of(project) : EMPTY)
        )),
        toArray(),
      )),
      map(projects => sortBy(projects, project => (project.label || project._id).toLowerCase()))
    );
  }

  getGroupedProjects(actions: PermissionAction[]): Observable<GroupOption[]> {
    return combineLatest([
      this.getProjectsWithPermissions(actions),
      this.getGroups().pipe(
        map(groups => new Map(groups.map(group => ([group._id, group] as [string, Group]))))
      ),
    ]).pipe(
      map(([projects, groupMap]) => {
        const groupedProjects = groupBy(projects, 'group');
        const groupOptions = Object.entries(groupedProjects).map(([groupId, projects]) => {
          const group = groupMap.get(groupId);
          const label = group ? (group.label || group._id) : groupId;
          return Object.assign({}, group, { label, projects: projects as Project[] });
        });
        return sortBy(groupOptions, group => group.label.toLowerCase());
      })
    );
  }

  /**
   * Get permission actions for a project even if it's not in the precomputed projectPermissions$ table.
   * Necessary for site admins with exhaustive mode off.
   */
  getProjectPermissions(projectId: string): Observable<PermissionAction[]> {
    if (!projectId) {
      throw new Error('projectId is not defined');
    }
    return combineLatest([
      this.projectPermissions$,
      this.profileService.getProfile(),
    ]).pipe(first(), map(([projectPermissions, profile]) => {
      if (projectId in projectPermissions) {
        return projectPermissions[projectId];
      }
      if (profile && SiteRole.isSiteAdmin(profile)) {
        return Object.values(PermissionAction);
      }
      return [];
    }));
  }

  /**
   * Reduce permission actions for a set of containers using an accumulator function.
   * union returns all possible permission actions
   * intersection returns all common permission actions
   */
  reducePermissions(containers: Container[] | SearchResult[], accumulator = union): Observable<PermissionAction[]> {
    return from(containers).pipe(
      mergeMap(container => this.getProjectPermissions(getContainerProjectId(container))),
      defaultIfEmpty([]),
      reduce((all, perms) => accumulator(all, perms)),
    );
  }

  async trackVisitedProject(projectId: string): Promise<void> {
    const visited = await firstValueFrom(this.visitedProjects$);
    if (visited[0] === projectId) {
      return;
    }
    try {
      const projects = await firstValueFrom(this.coreApi.users.updateVisitedProjects(projectId));
      this._visitedProjects$.next(projects);
    } catch (error) {
      this.logger.warn(error);
    }
  }
}
